import React from 'react';
import UAParser from 'ua-parser-js';

import { focusElement } from '../../../utils/focusUtils.ts';
import type { PluginConfig } from '../config-schema.ts';
import UserFeedbackModal, { type FeedbackTopic } from './UserFeedbackModal.tsx';

const USER_FEEDBACK_TYPE_PROMPT_WIDGET_ID = 'userFeedback/userFeedbackTypePrompt';

type ExtraTopic = FeedbackTopic & {
  choseEvent: string;
  isEnabled: (appConfig) => boolean;
  getProperties: (app) => Promise<Record<string, unknown>>;
};

// Topics offered after the built-in ones, each with its own question and metrics.
const EXTRA_TOPICS: ExtraTopic[] = [
  {
    value: 'feedbackTypeDirections',
    labelKey: 'userFeedback:My recent navigation',
    choseEvent: 'userFeedback/choseDirectionsType',
    isEnabled: appConfig => appConfig.plugins?.['online/getDirectionsFromTo']?.fullRoutePreviewEnabled === true,
    question: {
      id: 'stopReason',
      labelKey: 'userFeedback:How did your navigation end?',
      options: [
        { value: 'reachedDestination', labelKey: 'userFeedback:Reached destination' },
        { value: 'directionsConfusing', labelKey: 'userFeedback:Directions were confusing' },
        { value: 'alreadyKnewWay', labelKey: 'userFeedback:Already knew where to go' },
        { value: 'differentRoute', labelKey: 'userFeedback:Chose a different route' },
        { value: 'justChecking', labelKey: 'userFeedback:Just checking the route' },
        { value: 'other', labelKey: 'userFeedback:Other' },
      ],
    },
    getProperties: app => app.bus.get('navigation/getFeedbackMetrics'),
  },
];

function create(app, config: PluginConfig) {
  const extraTopics = EXTRA_TOPICS.filter(topic => topic.isEnabled(app.config));
  const findExtraTopic = (type: string) => extraTopics.find(topic => topic.value === type);

  function dismissFeedbackModal() {
    app.bus.send('layers/hide', { id: USER_FEEDBACK_TYPE_PROMPT_WIDGET_ID });

    // Restore focus to the submenu/user-menu trigger if present.
    // The trigger is stable even if the submenu is closed.
    const desktopUserMenuTrigger = document.querySelector(
      '[data-testid="UserMenuButtonDesktop"] button,button[aria-controls="submenu"]',
    );
    if (desktopUserMenuTrigger) {
      focusElement(desktopUserMenuTrigger);
      return;
    }

    // Fallbacks: focus a reasonable UI entry point.
    focusElement(
      '[data-testid="HeaderUserMenuButton"],[data-testid="UserMenuButtonDesktop"] button,button[aria-controls="submenu"]',
    );
  }

  async function goCustomerURL() {
    window.open(config.feedbackUrl, '_blank');
    const properties = await getProperties();
    properties.feedbackUrl = config.feedbackUrl;
    app.bus.send('appInsights/log', {
      name: 'userFeedback/visited',
      properties,
    });
  }

  async function submitForm(formData, type?: string) {
    const { feeling, comments } = formData;
    const topic = findExtraTopic(type);
    const topicProperties = topic ? await topic.getProperties(app).catch(() => ({})) : {};
    // Topic metrics go first so shared names such as url keep their feedback-time values.
    const properties = { ...topicProperties, ...(await getProperties()) };
    if (topic?.question) {
      properties.questionId = topic.question.id;
      properties.answer = formData.answer;
    } else {
      properties.feeling = feeling;
    }
    properties.comments = comments;
    app.bus.send('appInsights/log', {
      name: 'userFeedback/submission',
      properties,
    });
  }

  const getInstallId = () => {
    const { installId } = JSON.parse(localStorage.getItem(`ll_installId_${app.config.uuid}`)) || {};
    return installId;
  };

  async function getProperties(): Promise<Record<string, unknown>> {
    const shortAccountId = await app.bus.get('venueData/getAccountId');
    const parsedUserAgent = new UAParser().getResult();

    return {
      url: window.location.href,
      installId: getInstallId(),
      instanceName: app.config.name,
      shortAccountId,
      productVersion: app.info.wePkg.version,
      deviceTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      deviceOSName: parsedUserAgent.os.name,
      deviceOSVersion: parsedUserAgent.os.version,
      uiLocale: app.i18n().language,
      browserName: parsedUserAgent.browser.name,
      browserVersion: parsedUserAgent.browser.version,
      webConfigId: app.config.uuid,
    };
  }

  async function choseType(type) {
    const properties = await getProperties();
    app.bus.send('appInsights/log', {
      name:
        findExtraTopic(type)?.choseEvent ??
        (type === 'feedbackTypeMap' ? 'userFeedback/choseMapType' : 'userFeedback/choseJourneyType'),
      properties,
    });
  }

  const init = () => {
    const linkClickHandler = async ({ pageLocation } = {}) => {
      const properties = await getProperties();
      if (pageLocation) {
        properties.pageLocation = pageLocation;
      }
      app.bus.send('appInsights/log', {
        name: 'userFeedback/opened',
        properties,
      });
      app.bus.send('layers/show', { id: USER_FEEDBACK_TYPE_PROMPT_WIDGET_ID });
    };

    // UI entry point is now in the user menu; keep the feedback flow centralized here.
    app.bus.on('userFeedback/open', linkClickHandler);

    app.bus.send('layers/register', {
      id: USER_FEEDBACK_TYPE_PROMPT_WIDGET_ID,
      widget: () => (
        <UserFeedbackModal
          choseType={choseType}
          goCustomerURL={config.feedbackUrl ? goCustomerURL : null}
          onModalDismiss={dismissFeedbackModal}
          submitForm={submitForm}
          extraTopics={extraTopics}
          T={app.gt()}
          highZoomMaxWidth={app.config.mobileHighZoomBreakpoint}
          width={app.env.isMobile() ? 'calc(100% - 20px)' : '340px'}
        />
      ),
      layoutId: 'modalWindow',
      layoutName: 'fullscreen',
      isOverlay: true,
      show: false,
    });
  };

  return {
    init,
  };
}

export { create };
